import type { BoundingBox } from '@eye-able/types';
import type { RoundedCrop } from './types';

/** A rectangle expressed as percentages (0–100) of its container. */
export interface PercentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface NaturalSize {
  width: number;
  height: number;
}

/**
 * Legacy visual padding (in natural pixels) applied to highlight boxes drawn
 * over a full screenshot. The box is nudged up/left and grown so the outline
 * sits slightly outside the reported bounds. Preserved verbatim from the
 * original screenshot components to keep the rendered result identical.
 */
export const SCREENSHOT_BOX_PADDING = {
  left: 6,
  top: 6,
  width: 26,
  height: 13,
} as const;

/** Whether a bounding box carries the fields needed to be drawn. */
export function isDrawableBox(box?: BoundingBox | null): box is BoundingBox {
  return !!box && Object.keys(box).length > 0;
}

/**
 * Position a bounding box (natural px) as a percentage of the full image,
 * applying the legacy screenshot padding.
 */
export function toScreenshotRect(
  box: BoundingBox,
  natural: NaturalSize,
): PercentRect {
  const { left, top, width, height } = SCREENSHOT_BOX_PADDING;

  return {
    left: ((box.x - left) / natural.width) * 100,
    top: ((box.y - top) / natural.height) * 100,
    width: ((box.width + width) / natural.width) * 100,
    height: ((box.height + height) / natural.height) * 100,
  };
}

/**
 * Position a bounding box (natural px) as a percentage of a crop rectangle,
 * i.e. relative to a zoomed-in view centred on the box.
 */
export function toCropRelativeRect(
  box: BoundingBox,
  crop: RoundedCrop,
): PercentRect {
  return {
    left: ((box.x - crop.cropX) / crop.cropWidth) * 100,
    top: ((box.y - crop.cropY) / crop.cropHeight) * 100,
    width: (box.width / crop.cropWidth) * 100,
    height: (box.height / crop.cropHeight) * 100,
  };
}
